import {
  ENTRY_KIND,
  ENTRY_ROUTE_MARKERS,
  IS_ICP_PARAM,
  JOURNEY_ENTRY_STORAGE_KEY,
  REDIRECT_URI_PARAM,
  SHOP_PARAM,
  STORE_URL_PARAM,
} from './constants'
import { JourneyEntry } from './interface'

// ---------- entry capture (sessionStorage, never a write) ----------

let memoryEntry: JourneyEntry | null = null

const searchToParams = (search: string): URLSearchParams => {
  const index = search?.indexOf?.('?') ?? -1
  const raw = index >= 0 ? search.slice(index + 1) : search || ''
  return new URLSearchParams(raw)
}

// Every param on the login page, top-level plus the ones nested inside
// redirect_uri. Top-level wins on a clash, matching getParamOrNested.
export const collectEntryParams = (search: string): Record<string, string> => {
  const params: Record<string, string> = {}
  const topLevel = searchToParams(search)
  const redirectUri = topLevel.get(REDIRECT_URI_PARAM)
  if (redirectUri && redirectUri.includes('?')) {
    searchToParams(redirectUri).forEach((value, key) => {
      params[key] = value
    })
  }
  topLevel.forEach((value, key) => {
    params[key] = value
  })
  return params
}

export const getStoredJourneyEntry = (): JourneyEntry | null => {
  try {
    const raw = window?.sessionStorage?.getItem(JOURNEY_ENTRY_STORAGE_KEY)
    if (!raw) {
      return memoryEntry
    }
    return JSON.parse(raw)
  } catch {
    return memoryEntry
  }
}

export const captureLoginEntry = (
  search: string = window?.location?.search,
  pathname: string = window?.location?.pathname
): void => {
  const params = collectEntryParams(search)
  // A reload without the install params must not erase how they arrived.
  if (!params?.[SHOP_PARAM] && getStoredJourneyEntry()?.params?.[SHOP_PARAM]) {
    return
  }
  const entry: JourneyEntry = {
    params,
    query: search || '',
    pathname: pathname || '',
    captured_at: Date.now(),
  }
  memoryEntry = entry
  try {
    window?.sessionStorage?.setItem(
      JOURNEY_ENTRY_STORAGE_KEY,
      JSON.stringify(entry)
    )
  } catch {
    // Private mode / webview. memoryEntry already holds it.
  }
}

export const resetJourneyEntry = (): void => {
  memoryEntry = null
  try {
    window?.sessionStorage?.removeItem(JOURNEY_ENTRY_STORAGE_KEY)
  } catch {
    // Private mode. memoryEntry is already cleared.
  }
}

/**
 * Which Shopify door a redirect_uri came through, or null when it is neither.
 */
export const getEntryKind = (
  redirectUri?: string | null
): (typeof ENTRY_KIND)[keyof typeof ENTRY_KIND] | null => {
  if (!redirectUri) {
    return null
  }
  if (redirectUri.includes(ENTRY_ROUTE_MARKERS.install)) {
    return ENTRY_KIND.install
  }
  if (redirectUri.includes(ENTRY_ROUTE_MARKERS.admin_entry)) {
    return ENTRY_KIND.admin_entry
  }
  return null
}

export interface EntryTrackingProperties {
  via_shopify?: boolean
  shop?: string
  is_icp?: string
  store_url?: string
  entry_kind?: string
}

/**
 * The Shopify-entry fields every analytics event should carry for the rest of
 * the session, read from the login-page entry this tab captured. Until the
 * login page has been seen in this tab nothing is known and nothing is sent;
 * once it has, via_shopify is always stated, and the shop, is_icp (verbatim,
 * as the URL carried it), store_url and entry_kind follow when present. The
 * login visit itself sets these explicitly and its own values win on merge.
 */
export const getEntryTrackingProperties = (): EntryTrackingProperties => {
  const entry = getStoredJourneyEntry()
  if (!entry) {
    return {}
  }
  const params = entry?.params || {}
  const shop = params?.[SHOP_PARAM]
  const props: EntryTrackingProperties = { via_shopify: Boolean(shop) }
  if (!shop) {
    return props
  }
  props.shop = shop
  if (params?.[IS_ICP_PARAM]) {
    props.is_icp = params[IS_ICP_PARAM]
  }
  if (params?.[STORE_URL_PARAM]) {
    props.store_url = params[STORE_URL_PARAM]
  }
  const entryKind = getEntryKind(params?.[REDIRECT_URI_PARAM])
  if (entryKind) {
    props.entry_kind = entryKind
  }
  return props
}
